import { InstrumentUtils, LyricsMode, ScoreManager, ScoreProperties }
	from 'score_data';
import { AssessmentResults, AssessmentType, InteractionSettings, SFPlaybackDO,
	SFScoreDriver } from 'score_performance';
import { MusicTextures } from 'score_render';
import { SongDO, SongType } from '../data/SongDO';
import { Container } from 'pixi.js';
import { MicManager, MicOptions } from 'audio_input';
import { createEmitter } from 'ts_utils';
import { BrowserUtils } from 'ts_utils';
import { LocalStorageHelper } from '../utilities/LocalStorageHelper';
import { FingeringDisplayManager } from './fingerings/FingeringDisplayManager';
import { pixiAppHelper } from '../ui/pixi_apps';
import { Services } from '../services/Services';
import { RaAssessorFactory } from './RaAssessorFactory';

export class RAPlayer {
	private _driver:SFScoreDriver;
	private _container:Container;
	private _songDO:SongDO;
	private _needsFirstRender:boolean;
	
	private _scoreManager:ScoreManager;
	private _showNoteNames:boolean;
	private _fingeringDM:FingeringDisplayManager;
	
	private _tempoRatio:number = 1.0;
	private _metronomeVolume:number = 0;
	
	private _assessmentType:AssessmentType;
	private _useMicForRhythmAssessment:boolean;
	private _assessBeats:boolean = false;
	
	private _micPermissionGranted:boolean = false;
	
	public get event() { return this._emitter.event; }
	private _emitter = createEmitter<RAPlayerEvent>();
	
	constructor(driver:SFScoreDriver, container:Container){
		this._driver = driver;
		this._container = container;
		
		this._init();
	}
	
	public async openSong(songDO:SongDO):Promise<void> {
		this._songDO = songDO;
		
		this._songDO.musicXMLString = await Services.loadSongXml(songDO.path);
		
		let props = ScoreProperties.getNewScoreProperties();
		this._songDO.score = await this._driver.parseScore(
			songDO.musicXMLString, true, true, props);
		this._scoreManager.score = this._songDO.score;
		
		this._needsFirstRender = true;
		
		this.changeShowNotesAndFingerings(this._showNoteNames);
		await this.renderMusic();
		this._updateAssessmentSettings();
		this._updateTempo();
		this._updatePartPlaybackPresets();
	}
	
	public closeSong():void {
		this._container.removeChild(this._driver.scoreViewer);
		this._driver.unloadScore();
		this._songDO = null;
	}
	
	public async renderMusic():Promise<void> {
		let pixelsPS = this._calculateClipScoreScaling(this.viewWidth);
		await MusicTextures.createTextures(pixelsPS);
		let score = this._songDO.score;
		if (this._needsFirstRender){
			this._driver.renderNotation(score, this.viewWidth, this.viewHeight,
				[score.parts[0]]);
			this._needsFirstRender = false;
			this._container.addChild(this._driver.scoreViewer);
			let settings = new InteractionSettings();
			settings.enableNoteClickEvents = true;
			settings.enableDragScroll = true;
			this._driver.scoreViewer.setInteraction(settings);
		}
		else {
			this._driver.resetNotation(this.viewWidth, this.viewHeight);
		}
		
		this._driver.scoreViewer.forceScrolling = true;
		this._fingeringDM.setNewScore(this._driver.scoreViewer);
		
		// pixiApp.requestRender();
	}
	
	public changeShowNotesAndFingerings(show:boolean):void {
		this._showNoteNames = show;
		let el = document.querySelector("#_sp-fingeringsDiv") as HTMLDivElement;
		el.style.display = this._showNoteNames ? "" : "none";
		
		let part = this._songDO.score.parts[0];
		if (show){
			this._scoreManager.showNoteNames(part.staves[0]);
		}
		else if (part.staves[0].lyricsMode == LyricsMode.SHOW_LYRICS) {
			this._scoreManager.showLyrics();
		}
		else {
			this._scoreManager.hideLyrics();
		}
		
		pixiAppHelper.resizeToParent();
	}
	
	public async play():Promise<void> {
		if (BrowserUtils.isSafari()){
            await MicManager.get().initMicMediaStream();
        }
		
		let pdo = new SFPlaybackDO();
		pdo.tempoRatio = this._tempoRatio;
		pdo.metronomeVolume = this._metronomeVolume;
		pdo.playRepeats = false;
		pdo.assessmentType = this._assessmentType;
		pdo.countoffSeconds = 3.0;
		this._driver.preparePlayback(pdo);
		this._driver.clearAssessmentVisuals();
		this._driver.playPrepared(pdo, this._onPlaybackComplete);
	}
	
	public stop():void {
		this._driver.stop();
	}
	
	public getAssessmentResults():AssessmentResults {
		return this._driver.getAssessmentResults();
	}
	
	public clearAssessmentVisuals():void {
		this._driver.clearAssessmentVisuals();
	}
	
	public scrollToTop():void {
		this._driver.scoreViewer.scrollToTop(1.0);
	}
	
	private _init():void {
		this._scoreManager = new ScoreManager();
		this._fingeringDM = new FingeringDisplayManager();
		this._fingeringDM.setInstrument(this.instrument, true);
		this._showNoteNames = LocalStorageHelper.getShowFingerings();
	}
	
	private _onPlaybackComplete = () => {
		let ev = new RAPlayerEvent();
		ev.type = RAPlayerEventType.playbackComplete;
		this._emitter.emit(ev);
	};
	
	private async _updateAssessmentSettings():Promise<void> {
		if (this._songDO.type === SongType.RecorderAssessment){
			this._assessmentType = AssessmentType.pitch;
			this._driver.setInstrument(this.instrument);
			await this._getMicrophonePermission();
			this._metronomeVolume = 0;
		}
		else if (this._songDO.type === SongType.RhythmAssessment){
			this._assessmentType = AssessmentType.rhythmKeyboard;
			this._useMicForRhythmAssessment = false;
			(this._driver.assessorFactory as RaAssessorFactory).assessBeats =
				this._assessBeats;
			this._songDO.score.parts[0].volume = 0;
			this._metronomeVolume = 1.0;
		}
	}
	
	private _updateTempo():void {
		//apply any tempo override
		if (this._songDO.overrideTempo != null){
			let stacks = this._songDO.score.getMeasureStacks();
			let tempoRatio = this._songDO.overrideTempo / 
				stacks[0].tempoMarkers[0].tempo;
			stacks.forEach((s) => {
				if (s.tempoMarkers != null){
					s.tempoMarkers.forEach((tm) => tm.tempo *= tempoRatio);
				}
			});
		}
	}
	
	private _updatePartPlaybackPresets():void {
		for (let part of this._songDO.score.parts){
			if (part.midiChannel !== 10){
				//guitar
				if (part.midiPreset >= 24 && part.midiPreset <= 31){
					part.midiPreset = 24;
				}
				//bass
				else if (part.midiPreset >= 32 && part.midiPreset <= 39){
					part.midiPreset = 33;
				}
				//piano default
				else {
					part.midiPreset = 0;
				}
			}
			else {
				//unpitched percussion
				part.midiBank = 128;
				part.midiPreset = 0;
			}
		}
	}
	
	private async _getMicrophonePermission():Promise<void> {
		if (!this._micPermissionGranted){
			let micOptions = new MicOptions();
			await this._driver.enableMicrophone(micOptions);
			this._micPermissionGranted = true;
		}
	}
	
	private _calculateClipScoreScaling(width:number):number {
		if (width >= 1000) return 10;
		else if (width >= 800) return 9;
		else if (width >= 600) return 8;
		else return 7;
	}
	
	public get songDO():SongDO { return this._songDO; }
	
	public get viewWidth():number {
		return pixiAppHelper.app.canvas.parentElement.clientWidth;
	}
	
	public get viewHeight():number {
		return pixiAppHelper.app.canvas.parentElement.clientHeight;
	}
	
	public get showNoteNames():boolean {
		return this._showNoteNames;
	}
	
	public get tempoRatio() { return this._tempoRatio; }
	public set tempoRatio(value:number) { this._tempoRatio = value; }
	
	public get assessmentType():AssessmentType { return this._assessmentType; }
	
	public get instrument() { return InstrumentUtils.instruments[0]; }
	
	public get isPlaying():boolean { return this._driver.isPlaying; }
	
	public get scoreLoaded():boolean {
		return (this._driver.scoreViewer != null && this._songDO != null);
	}
}

export class RAPlayerEvent {
	public type:RAPlayerEventType;
}

export enum RAPlayerEventType {
	playbackComplete
}